import React, { useEffect, useRef } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import VcompParticipateNotification from "../../../../components/dashboard/Vcomp/VcompParticipateNotification";
import VcompManageNotification from "../../../../components/dashboard/Vcomp/VcompManageNotification";
import { useDispatch, useSelector } from "react-redux";
import { checkPermission } from "../../../../app/utils/helpers";
import { config } from "../../../../app/utils/config";
import { getVcompNotifications } from "../../dashboardSlice";

/* ── shared card styles matching DashboardSafeCard aesthetic ── */
const vcompCardSx = {
    borderRadius: 4,
    boxShadow: "0 22px 44px rgba(15, 23, 42, 0.14)",
    border: "1px solid rgba(148, 163, 184, 0.28)",
    background:
        "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 248, 255, 0.92) 50%, rgba(255, 255, 255, 0.94) 100%)",
    height: "100%",
    overflow: "hidden",
    position: "relative",
    transition:
        "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
    "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 30px 68px rgba(15, 23, 42, 0.18)",
        borderColor: "rgba(148, 163, 184, 0.45)",
    },
};

const radialOverlaySx = (accentRgba) => ({
    position: "absolute",
    inset: 0,
    background: `
        radial-gradient(circle at 12% 12%, ${accentRgba}, transparent 50%),
        radial-gradient(circle at 88% 12%, rgba(15, 23, 42, 0.05), transparent 45%),
        radial-gradient(circle at 50% 100%, rgba(15, 23, 42, 0.04), transparent 55%)
    `,
    pointerEvents: "none",
});

const accentBarSx = (gradient) => ({
    height: 6,
    background: gradient,
});

export default function DashboardVcomp({ compact = false, showEmpty = true }) {
    const dispatch = useDispatch();
    const fetchedRef = useRef(false);
    const canViewVcomp = checkPermission(config.scopes.vcomp, config.permissionTypes.view);
    const participateProjects = useSelector(
        (state) => state.dashboard.vcompParticipateNotification
    );
    const managedProjects = useSelector(
        (state) => state.dashboard.vcompManageNotification
    );
    const showParticipations = Array.isArray(participateProjects) && participateProjects.length > 0;
    const showManagement = Array.isArray(managedProjects) && managedProjects.length > 0;

    useEffect(() => {
        if (canViewVcomp && !fetchedRef.current) {
            dispatch(getVcompNotifications());
            fetchedRef.current = true;
        }
    }, [canViewVcomp, dispatch]);

    const renderParticipationsCard = () => (
        <Card sx={vcompCardSx}>
            <Box sx={radialOverlaySx("rgba(37, 99, 235, 0.08)")} />
            <Box
                sx={accentBarSx(
                    "linear-gradient(90deg, #2563eb, rgba(255, 255, 255, 0.85), #2563eb)"
                )}
            />
            <CardContent sx={{ p: { xs: 2, md: 3 }, position: "relative" }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 2,
                        letterSpacing: "-0.01em",
                        color: "rgba(15, 23, 42, 0.88)",
                    }}
                >
                    Vcomp - Participations
                </Typography>
                <VcompParticipateNotification />
            </CardContent>
        </Card>
    );

    const renderManagementCard = () => (
        <Card sx={vcompCardSx}>
            <Box sx={radialOverlaySx("rgba(255, 144, 21, 0.08)")} />
            <Box
                sx={accentBarSx(
                    "linear-gradient(90deg, #FF9015, rgba(255, 193, 0, 0.85), #FF9015)"
                )}
            />
            <CardContent sx={{ p: { xs: 2, md: 3 }, position: "relative" }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        mb: 2,
                        letterSpacing: "-0.01em",
                        color: "rgba(15, 23, 42, 0.88)",
                    }}
                >
                    Vcomp - Management
                </Typography>
                <VcompManageNotification />
            </CardContent>
        </Card>
    );

    const renderEmptyState = () => {
        if (!showEmpty) return null;
        return (
            <Card
                sx={{
                    ...vcompCardSx,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                }}
            >
                <Box sx={radialOverlaySx("rgba(148, 163, 184, 0.06)")} />
                <Typography
                    variant="body2"
                    sx={{
                        color: "rgba(107, 114, 128, 0.8)",
                        fontWeight: 500,
                        position: "relative",
                    }}
                >
                    Aucune participation Vcomp disponible.
                </Typography>
            </Card>
        );
    };

    if (!canViewVcomp) {
        return null;
    }

    if (compact) {
        if (!showParticipations && !showManagement) {
            return renderEmptyState();
        }
        if (showParticipations && showManagement) {
            return (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                        gap: 2,
                        "& > *": { minWidth: 0 },
                    }}
                >
                    {renderParticipationsCard()}
                    {renderManagementCard()}
                </Box>
            );
        }
        if (showParticipations) {
            return renderParticipationsCard();
        }
        if (showManagement) {
            return renderManagementCard();
        }
        return renderEmptyState();
    }

    if (!showParticipations && !showManagement) {
        return renderEmptyState();
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                gap: 2,
                "& > *": { minWidth: 0 },
            }}
        >
            {showParticipations && renderParticipationsCard()}
            {showManagement && renderManagementCard()}
        </Box>
    );
}
